import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ViewSchedulerTable.module.css";
import Button from "../../../common-ui-components/components/button/Button";
import API from "common-ui-components/components/utils/API";
import parseCron from "../../../common-ui-components/components/schedule/parseCron";
import ResponseLoader from "common-ui-components/components/ResponseLoader/ResponseLoader";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";
import { notify } from "common-ui-components/components/notification/Notification";
import {
  Failure,
  Success,
} from "common-ui-components/components/notification/templates";
import editIcon from "common-ui-components/components/icons/edit-pencil.svg";
import mailIcon from "common-ui-components/components/icons/mail.svg";

export const decodeCornParserValue = (expression) => {
  const weeks = ["First", "Second", "Third", "Fourth", "Fifth"];
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dailyFormats = ["Everyday", "Alternative days", "Week days"];

  const frequency = `${parseCron(expression).frequency}`;
  let repeat = parseCron(expression).repeat;
  let occurs = parseCron(expression).occurs;

  if (frequency === "Monthly") {
    repeat = repeat.map((item) => months[item - 1]).join(", ");
    occurs = `${weeks[occurs[0]]} ${days[occurs[1]]}`;
  }
  if (frequency === "Weekly") {
    repeat = repeat.map((item) => days[item]).join(", ");
    occurs = null;
  }
  if (frequency === "Daily") {
    repeat = repeat.map((item) => dailyFormats[item]).join(", ");
    occurs = null;
  }
  return { frequency, repeat, occurs };
};

export const handlePause = async (jobId, updateFunction = null) => {
  try {
    const response = await API.get(`/pauseJob?jobId=${jobId}`);
    if (response.status.success) {
      notify(<Success>The schedule was paused successfully</Success>);
      if (updateFunction) updateFunction();
    } else {
      notify(<Failure>Failed in pausing the schedule</Failure>);
    }
  } catch {
    console.error("Error in pausing schedule");
  }
};

export const handleResume = async (jobId, updateFunction = null) => {
  try {
    const response = await API.get(`/resumeJob?jobId=${jobId}`);
    if (response.status.success) {
      notify(<Success>The schedule was resumed successfully</Success>);
      if (updateFunction) updateFunction();
    } else {
      notify(<Failure>Failed in resuming the schedule</Failure>);
    }
  } catch {
    console.error("Error in resuming schedule");
  }
};

export const handleRemove = async (jobId, updateFunction = null) => {
  try {
    const response = await API.delete(`/deleteJob?jobId=${jobId}`);
    if (response.status.success) {
      notify(<Success>The schedule was removed successfully</Success>);
      if (updateFunction) updateFunction();
    } else {
      notify(<Failure>Failed in removing the schedule</Failure>);
    }
  } catch {
    console.error("Error in resuming schedule");
  }
};

export const handleTriggerNow = async (jobId, updateFunction = null) => {
  try {
    const response = await API.get(`/trigger-now?jobId=${jobId}`);
    if (response.status.success) {
      notify(<Success>The schedule was triggered successfully</Success>);
      if (updateFunction) updateFunction();
    } else {
      notify(<Failure>Failed in triggering the schedule</Failure>);
    }
  } catch {
    console.error("Error in resuming schedule");
  }
};

const SkeletonViewScheduler = () => (
  <>
    <div className={styles.pageHeader}>
      <div className={styles.skeletonTitle}></div>
      <div className={styles.skeletonSubtitle}></div>
    </div>

    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.skeletonCardTitle}></div>

        <div className={styles.skeletonButtons}>
          <div className={styles.skeletonButton}></div>
          <div className={styles.skeletonButton}></div>
          <div className={styles.skeletonButton}></div>
          <div className={styles.skeletonButton}></div>
        </div>
      </div>

      <div className={styles.table}>
        {[...Array(9)].map((_, index) => (
          <div key={index} className={styles.row}>
            <div className={styles.label}>
              <div className={styles.skeletonLabel}></div>
            </div>
            <div className={styles.value}>
              <div className={styles.skeletonValue}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);

const ViewSchedulers = ({ home = false, initialResponse = null }) => {
  const [schedules, setSchedules] = useState([]);
  const [response, setResponse] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // user management
  const { checkRestriction } = useRestrictionChecker();
  const canViewSchedule = !checkRestriction(
    "VIEW_SCHEDULED_SCAN",
    home,
    "page",
  );

  const handlePauseOperation = async (index, jobId) => {
    if (checkRestriction("EDIT_A_SCHEDULED_SCAN")) return;

    const status = schedules[index].Status;

    if (status === "Active") handlePause(jobId);
    else handleResume(jobId);

    setSchedules((prevSchedules) =>
      prevSchedules.map((schedule, ind) => {
        if (index !== ind) return schedule;
        return {
          ...schedule,
          Status: status === "In Active" ? "Active" : "In Active",
        };
      }),
    );
  };

  // todo: the param should only send the jobId of the selected scheduler
  const handleEdit = (jobId) => {
    if (checkRestriction("EDIT_A_SCHEDULED_SCAN")) return;
    navigate(`../edit?jobId=${jobId}`);
  };

  useEffect(() => {
    const getSchedules = async () => {
      try {
        setLoading(true);
        const response = initialResponse
          ? { status: { success: true }, body: initialResponse }
          : await API.get("/scheduledJob?methodId=1");

        if (response.status.success) {
          const schedule = response.body.map((item) => {
            const {
              nextFireTime,
              createdBy,
              createdOn,
              updatedBy,
              expression,
              updatedOn,
              username,
              status,
              emailIds,
            } = item;

            const { frequency, repeat, occurs } =
              decodeCornParserValue(expression);

            return {
              name: "Schedule Device scan",
              "Next schedule time": nextFireTime,
              Frequency: frequency,
              "Occurs on": occurs,
              Repeats: repeat,
              Status: status ? "Active" : "In Active",
              "Scan type": "Full scan",
              "Created by": `${createdBy} on ${createdOn}`,
              "Last modified by": updatedBy
                ? `${updatedBy} on ${updatedOn}`
                : "Not modified",
              Notification: emailIds
                ? emailIds.join(", ")
                : "No one to be notified",
            };
          });
          setSchedules(schedule);
          setResponse(response.body);
        } else {
          console.error(response.status.message);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (canViewSchedule) getSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialResponse, navigate]);

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.wrapper}>
          <SkeletonViewScheduler />
        </div>
      </div>
    );
  }

  // todo: handle multiple scheduled details
  return (
    <div className={styles.pageWrapper}>
      <div className={styles.wrapper}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Configure Scheduler</h1>
          <p className={styles.pageSubtitle}>
            Configure and review device scan schedules, status, and notification
            settings.
          </p>
        </div>

        {schedules.length !== 0 ? (
          schedules.map((schedule, index) => {
            const jobId = response[index]?.jobId || 0;

            return (
              <div key={jobId} className={styles.card}>
                <div className={styles.header}>
                  <h3 className={styles.title}>Schedule Device scan</h3>

                  <div className={styles.actions}>
                    <Button
                      className={styles.outlineButton}
                      type="button"
                      onClick={() => handlePauseOperation(index, jobId)}
                    >
                      {schedule.Status === "Active" ? "Pause" : "Resume"}
                    </Button>

                    <Button
                      className={styles.outlineButton}
                      type="button"
                      onClick={() => handleTriggerNow(jobId)}
                    >
                      Trigger Now
                    </Button>

                    <Button
                      className={styles.removeButton}
                      type="button"
                      onClick={() => handleRemove(jobId)}
                    >
                      Remove
                    </Button>

                    {!home && (
                      <Button
                        className={styles.editButton}
                        onClick={() => handleEdit(jobId)}
                      >
                        <img src={editIcon} alt="" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                <div className={styles.table}>
                  <div className={styles.row}>
                    <div className={styles.label}>Next schedule time</div>
                    <div className={styles.value}>
                      {String(schedule["Next schedule time"])}
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Frequency</div>
                    <div className={styles.value}>{schedule.Frequency}</div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Occurs on</div>
                    <div className={styles.value}>
                      {schedule["Occurs on"] || "--"}
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Repeats</div>
                    <div className={styles.value}>{schedule.Repeats}</div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Status</div>
                    <div className={styles.value}>
                      <span
                        className={
                          schedule.Status === "Active"
                            ? styles.activeBadge
                            : styles.inactiveBadge
                        }
                      >
                        {schedule.Status.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Scan type</div>
                    <div className={styles.value}>{schedule["Scan type"]}</div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Created by</div>
                    <div className={styles.value}>{schedule["Created by"]}</div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Last modified by</div>
                    <div className={styles.value}>
                      {schedule["Last modified by"]}
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.label}>Notification</div>
                    <div className={styles.value}>
                      {schedule.Notification &&
                      schedule.Notification !== "No one to be notified" ? (
                        <div className={styles.notification}>
                          <img src={mailIcon} alt="Mail" />
                          <span>{schedule.Notification}</span>
                        </div>
                      ) : (
                        "--"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.empty}>No Scan Schedules done yet</div>
        )}
      </div>
    </div>
  );
};

export default ViewSchedulers;
